import { memo, useMemo } from "react";

import { Box, Grid, Typography } from "@mui/material";

import BszSectionCard from "../../../1_BSZFinderApp/components/sectionCards/BszSectionCard";
import { useAppContext } from "../../../../2_ksbHub/context/appContext";

/**
 * =========================================================
 * ✅ ASKOMA SUMMARY SECTION
 * =========================================================
 */
function AskomaEnergySection({ viewMode, data = [] }) {
  const { isMobile } = useAppContext();
  const viewLabels = {
    today: "Heute",
    week: "Woche",
    month: "Monat",
    all: "Alle",
  };

  const stats = useMemo(() => {
    if (!data.length) {
      return null;
    }

    const totalFeedin =
      data.reduce(
        (sum, row) =>
          sum +
          (row.feedin < 0 ? Math.abs(Number(row.feedin || 0)) : 0) / 60 +
          Number(row.heater_load || 0) / 60,
        0,
      ) / 1000;

    const totalAskoma =
      data.reduce((sum, row) => sum + Number(row.heater_load || 0) / 60, 0) /
      1000;

    const temperatures = data.map((row) => Number(row.temperature || 0));

    const maxTemperature = Math.max(...temperatures);
    const minTemperature = Math.min(...temperatures);

    const minutesAbove60 = data.filter(
      (row) => Number(row.temperature) >= 60,
    ).length;

    const minutesBelow50 = data.filter(
      (row) => Number(row.temperature) < 50,
    ).length;

    const coverage =
      totalFeedin > 0 ? ((totalAskoma / totalFeedin) * 100).toFixed(1) : 0;

    return {
      totalFeedin,
      totalAskoma,
      maxTemperature,
      minTemperature,
      hoursAbove60: (minutesAbove60 / 60).toFixed(1),
      hoursBelow50: (minutesBelow50 / 60).toFixed(1),
      coverage,
    };
  }, [data]);

  if (!stats) {
    return null;
  }

  const detailCards = [
    {
      id: 0,
      label: "PV Überschuss (in kwh)",
      value: stats.totalFeedin.toFixed(2).toLocaleString(),
      info: "Summierter PV-Überschuss",
      unit: "kWh",
    },
    {
      id: 1,
      label: "Askoma Verbrauch",
      value: stats.totalAskoma.toFixed(2).toLocaleString(),
      info: "Summierte Heizleistung",
      unit: "kWh",
    },
    {
      id: 2,
      label: "Max Temperatur",
      value: stats.maxTemperature.toFixed(1),
      info: `${stats.hoursAbove60} h ≥ 60 °C`,
      unit: "°C",
    },
    {
      id: 3,
      label: "Min Temperatur",
      value: stats.minTemperature.toFixed(1),
      info: `${stats.hoursBelow50} h < 50 °C`,
      unit: "°C",
    },
    {
      id: 4,
      label: "Nutzungsgrad",
      value: stats.coverage,
      info: "Askoma Verbrauch / PV Überschuss",
      unit: "%",
    },
  ];

  const mobileCards = [
    {
      id: 0,
      label: "PV Überschuss (in kwh)",
      value: stats.totalFeedin.toFixed(2).toLocaleString(),
      info: "Summierter PV-Überschuss",
      unit: "kWh",
    },
    {
      id: 1,
      label: "Askoma Verbrauch",
      value: stats.totalAskoma.toFixed(2).toLocaleString(),
      info: "Summierte Heizleistung",
      unit: "kWh",
    },
    {
      id: 4,
      label: "Nutzungsgrad",
      value: stats.coverage,
      info: "Askoma Verbrauch / PV Überschuss",
      unit: "%",
    },
  ];

  const cards = isMobile ? mobileCards : detailCards;

  return (
    <BszSectionCard
      // title={"Überblick · " + viewLabels[viewMode]}
      title={"Zusammenfassung"}
      defaultExpanded={!isMobile}
    >
      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid item xs={6} sm={6} md={2.4} key={card.id}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                height: "100%",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {card.label}
              </Typography>

              <Typography
                variant="h4"
                sx={{
                  mt: 1,
                  fontWeight: 600,
                }}
              >
                {card.value} {card.unit}
              </Typography>

              {!isMobile && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    opacity: 0.7,
                  }}
                >
                  {card.info}
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </BszSectionCard>
  );
}

export default memo(AskomaEnergySection);
